import React, {useState, useEffect} from 'react'
import axios from 'axios'
import {useReduxAuth} from '../../../auth'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {KTCard as Card, KTCardBody as CardBody} from '../../../../../_metronic/helpers'
import './AdminDashboard.css'

// ──────────────────  custom charts  ──────────────────
import DonutChart from '../../components/charts/DonutChart'
import GaugeChart from '../../components/charts/GaugeChart'
import ProgressBar from '../../components/charts/ProgressBar'
import StatCard from './StatCard'

// ──────────────────  types  ──────────────────
interface TotalUsers {
  totalUsers: number
}
interface SubscriptionStatus {
  subscription_status: string | null
  count: number
}
interface SubscriptionData {
  subscriptionData: SubscriptionStatus[]
}
interface DevicesData {
  totalDevices: number
  allocatedDevicesCount: number
  availableDevices: number
}
interface PurchasedVsRental {
  purchasedDevices: number
  rentalDevices: number
  loanedDevices?: number
  totalDevices: number
}
interface PatientsData {
  totalPatients: number
  activePatients: number
  inactivePatients: number
}
interface SubadminsData {
  totalSubadmins: number
}
interface DeviceUtilizationData {
  utilizationRate: number
  totalDaysAllocated: number
  totalPossibleDeviceDays: number
  periodDays: number
}
interface PatientComplianceData {
  complianceRate: number
  actualReadingsCount: number
  expectedReadingsCount: number
}

const STATUS_COLORS: Record<string, string> = {
  active: '#00C49F',
  inactive: '#666666',    
  trial: '#FFBB28',       
  cancelled: '#FF8042',   
  past_due: '#FF4D4F',
  null: '#000000', 
}

// ──────────────────  component  ──────────────────
const AdminDashboard: React.FC = () => {
  // initialise auth context (return value not needed in this file)
  useReduxAuth()

  const [isLoading, setIsLoading] = useState(true)
  const [totalUsers, setTotalUsers] = useState<TotalUsers | null>(null)
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null)
  const [devicesData, setDevicesData] = useState<DevicesData | null>(null)
  const [purchasedVsRental, setPurchasedVsRental] = useState<PurchasedVsRental | null>(null)
  const [patientsData, setPatientsData] = useState<PatientsData | null>(null)
  const [subadminsData, setSubadminsData] = useState<SubadminsData | null>(null)
  const [deviceUtilization, setDeviceUtilization] = useState<DeviceUtilizationData | null>(null)
  const [patientCompliance, setPatientCompliance] = useState<PatientComplianceData | null>(null)

  // ──────────────────  fetch dashboard data  ──────────────────
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true)
        const results = await Promise.allSettled([
          axios.get(API_ENDPOINTS.ADMIN_TOTAL_USERS()),
          axios.get(API_ENDPOINTS.ADMIN_SUBSCRIPTION_STATUS()),
          axios.get(API_ENDPOINTS.ADMIN_TOTAL_DEVICES()),
          axios.get(API_ENDPOINTS.ADMIN_PURCHASED_VS_RENTAL()),
          axios.get(API_ENDPOINTS.ADMIN_TOTAL_PATIENTS()),
          axios.get(API_ENDPOINTS.ADMIN_TOTAL_SUBADMINS()),
          axios.get(API_ENDPOINTS.ADMIN_DEVICE_UTILIZATION()),
          axios.get(API_ENDPOINTS.ADMIN_PATIENT_COMPLIANCE()),
        ])

        const [
          usersRes,
          subscriptionRes,
          devicesRes,
          purchasedVsRentalRes,
          patientsRes,
          subadminsRes,
          deviceUtilizationRes,
          patientComplianceRes,
        ] = results

        if (usersRes.status === 'fulfilled') setTotalUsers(usersRes.value.data.data)
        if (subscriptionRes.status === 'fulfilled')
          setSubscriptionData(subscriptionRes.value.data.data)
        if (devicesRes.status === 'fulfilled') setDevicesData(devicesRes.value.data.data)
        if (purchasedVsRentalRes.status === 'fulfilled')
          setPurchasedVsRental(purchasedVsRentalRes.value.data.data)
        if (patientsRes.status === 'fulfilled') setPatientsData(patientsRes.value.data.data)
        if (subadminsRes.status === 'fulfilled') setSubadminsData(subadminsRes.value.data.data)
        if (deviceUtilizationRes.status === 'fulfilled')
          setDeviceUtilization(deviceUtilizationRes.value.data.data)
        if (patientComplianceRes.status === 'fulfilled')
          setPatientCompliance(patientComplianceRes.value.data.data)
      } catch (err) {
        console.error('Error fetching admin dashboard data:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  // ──────────────────  format helpers  ──────────────────
  const formatSubscriptionData = () =>
    subscriptionData?.subscriptionData.map((s) => ({
      name: s.subscription_status ?? 'No Status',
      value: s.count,
      color: STATUS_COLORS[s.subscription_status ?? 'null'],
    })) ?? []

  const formatPurchasedVsRental = () => {
    const purchasedValue = purchasedVsRental?.purchasedDevices || 0
    const rentalValue = purchasedVsRental?.rentalDevices || 0
    const loanedValue = purchasedVsRental?.loanedDevices || 0

    return purchasedVsRental
      ? [
          {
            name: 'Purchased',
            value: purchasedValue === 0 ? 0.1 : purchasedValue,
            color: '#0088FE',
            actualValue: purchasedValue,
          },
          {
            name: 'Rental',
            value: rentalValue === 0 ? 0.1 : rentalValue,
            color: '#00C49F',
            actualValue: rentalValue,
          },
          {
            name: 'Loaned',
            value: loanedValue === 0 ? 0.1 : loanedValue,
            color: '#FFBB28',
            actualValue: loanedValue,
          },
        ]
      : []
  }

  const formatPatientsData = () =>
    patientsData
      ? [
          {name: 'Active', value: patientsData.activePatients, color: '#00C49F'},
          {name: 'Inactive', value: patientsData.inactivePatients, color: '#FF8042'},
        ]
      : []

  const formatDeviceAllocationData = () =>
    devicesData
      ? [
          {name: 'Allocated', value: devicesData.allocatedDevicesCount, color: '#0088FE'},
          {name: 'Available', value: devicesData.availableDevices, color: '#00C49F'},
        ]
      : []

  const formatReadingComplianceData = () =>
    patientCompliance
      ? [
          {
            name: 'Readings',
            actual: patientCompliance.actualReadingsCount,
            expected: patientCompliance.expectedReadingsCount,
            complianceRate: patientCompliance.complianceRate,
          },
        ]
      : []

  // ──────────────────  loading state  ──────────────────
  if (isLoading) {
    return <div className='d-flex justify-content-center p-10'>Loading dashboard data…</div>
  }
  return (
    <>
      {/* ───── first row: big-number cards via StatCard ───── */}
      <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
        {/* Total Users */}
        <div className='col-xl-3 col-md-6'>
          <StatCard
            title='Total Users'
            value={totalUsers?.totalUsers ?? 0}
            iconClass='bi bi-people'
            theme='primary'
            progressPct={(totalUsers?.totalUsers ?? 0) / 2}
          />
        </div>

        {/* Total Patients */}
        <div className='col-xl-3 col-md-6'>
          <StatCard
            title='Total Patients'
            value={patientsData?.totalPatients ?? 0}
            iconClass='bi bi-heart-pulse'
            theme='info'
            secondary={[
              {
                label: 'Active',
                value: patientsData?.activePatients ?? 0,
                colorClass: 'text-success',
              },
              {
                label: 'Inactive',
                value: patientsData?.inactivePatients ?? 0,
                colorClass: 'text-danger',
              },
              {
                label: 'Ratio',
                value:
                  patientsData?.activePatients && patientsData?.totalPatients
                    ? `${Math.round(
                        (patientsData.activePatients / patientsData.totalPatients) * 100
                      )}%`
                    : '0%',
                colorClass: 'text-primary',
              },
            ]}
          />
        </div>

        {/* Total Devices */}
        <div className='col-xl-3 col-md-6'>
          <StatCard
            title='Total Devices'
            value={devicesData?.totalDevices ?? 0}
            iconClass='bi bi-device-hdd'
            theme='success'
            secondary={[
              {
                label: 'Allocated',
                value: devicesData?.allocatedDevicesCount ?? 0,
                colorClass: 'text-primary',
              },
              {
                label: 'Available',
                value: devicesData?.availableDevices ?? 0,
                colorClass: 'text-success',
              },
              {
                label: 'Usage',
                value:
                  devicesData?.allocatedDevicesCount && devicesData?.totalDevices
                    ? `${Math.round(
                        (devicesData.allocatedDevicesCount / devicesData.totalDevices) * 100
                      )}%`
                    : '0%',
                colorClass: 'text-primary',
              },
            ]}
          />
        </div>

        {/* Total Sub-admins */}
        <div className='col-xl-3 col-md-6'>
          <StatCard
            title='Total Sub-admins'
            value={subadminsData?.totalSubadmins ?? 0}
            iconClass='bi bi-person-badge'
            theme='warning'
            progressPct={(subadminsData?.totalSubadmins ?? 0) * 25}
            dataUnavailable={subadminsData === null}
          />
        </div>
      </div>

      {/* ───── second row: donut charts ───── */}
      <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
        {/* Subscription Status */}
        <div className='col-xl-6'>
          <Card className='h-100 card-shadow border rounded-5 hover-elevate-up'>
            <div className='card-header border-0 pt-5 bg-light-primary bg-opacity-50'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>
                  Subscription Status Distribution
                </span>
                <span className='text-muted fw-semibold fs-7'>
                  Overview of subscription statuses
                </span>
              </h3>
            </div>
            <CardBody className='pt-5'>
              <DonutChart
                data={formatSubscriptionData()}
                height={320}
                innerRadius={60}
                outerRadius={120}
                itemLabel='users'
              />
            </CardBody>
          </Card>
        </div>

        {/* Purchased vs Rental */}
        <div className='col-xl-6'>
          <Card className='h-100 card-shadow rounded-5 border hover-elevate-up'>
            <div className='card-header border-0 pt-5 bg-light-success bg-opacity-50'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>Device Ownership Breakdown</span>
                <span className='text-muted fw-semibold fs-7'>
                  Breakdown of device ownership types
                </span>
              </h3>
            </div>
            <CardBody className='pt-5'>
              <DonutChart
                data={formatPurchasedVsRental()}
                height={320}
                innerRadius={60}
                outerRadius={120}
                itemLabel='devices'
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {/* ───── third row: gauges ───── */}
      <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
        {/* Device Utilisation */}
        <div className='col-xl-6'>
          <Card className='h-100 card-shadow rounded-5 border hover-elevate-up'>
            <div className='card-header border-0 pt-5 bg-light-primary bg-opacity-50'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>Device Utilisation Rate</span>
                <span className='text-muted fw-semibold fs-7'>
                  Last {deviceUtilization?.periodDays ?? 30} days performance
                </span>
              </h3>
            </div>
            <CardBody>
              <div className='d-flex flex-column align-items-center'>
                <GaugeChart
                  value={deviceUtilization?.utilizationRate ?? 0}
                  mainText={(deviceUtilization?.utilizationRate ?? 0).toFixed(1)}
                  subText='Utilisation Rate'
                  size={220}
                  primaryColor='#3699FF'
                  gradientStartColor='#3699FF'
                  gradientEndColor='#5ccdff'
                  backgroundStartColor='#e0e6ff'
                  backgroundEndColor='#f5f8ff'
                  gaugeId='deviceUtilisationGauge'
                  shadowColor='rgba(54,153,255,0.5)'
                />

                <div className='d-flex flex-column text-center mt-5'>
                  <div className='d-flex justify-content-between'>
                    <div className='px-4 py-2 bg-light-primary rounded-2 me-3'>
                      <div className='fs-7 text-gray-600'>Days Allocated</div>
                      <div className='fs-4 fw-bold text-primary'>
                        {deviceUtilization?.totalDaysAllocated ?? 0}
                      </div>
                    </div>
                    <div className='px-4 py-2 bg-light-info rounded-2'>
                      <div className='fs-7 text-gray-600'>Total Possible Days</div>
                      <div className='fs-4 fw-bold text-info'>
                        {deviceUtilization?.totalPossibleDeviceDays ?? 0}
                      </div>
                    </div>
                  </div>

                  <div className='mt-5'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <span className='text-muted fs-7'>0%</span>
                      <span className='text-muted fs-7'>25%</span>
                      <span className='text-muted fs-7'>50%</span>
                      <span className='text-muted fs-7'>75%</span>
                      <span className='text-muted fs-7'>100%</span>
                    </div>
                    <div className='h-8px bg-light rounded'>
                      <div
                        className='h-8px bg-primary rounded'
                        style={{
                          width: `${Math.min(deviceUtilization?.utilizationRate ?? 0, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Patient Compliance */}
        <div className='col-xl-6'>
          <Card className='h-100 card-shadow rounded-5 border hover-elevate-up'>
            <div className='card-header border-0 pt-5 bg-light-success bg-opacity-50'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>Patient Reading Compliance</span>
                <span className='text-muted fw-semibold fs-7'>
                  Overall reading adherence metrics
                </span>
              </h3>
            </div>
            <CardBody>
              <div className='d-flex flex-column align-items-center'>
                <GaugeChart
                  value={patientCompliance?.complianceRate ?? 0}
                  mainText={(patientCompliance?.complianceRate ?? 0).toFixed(1)}
                  subText='Compliance Rate'
                  size={220}
                  primaryColor='#50CD89'
                  gradientStartColor='#50CD89'
                  gradientEndColor='#47d990'
                  backgroundStartColor='#e6faf0'
                  backgroundEndColor='#f5fff9'
                  gaugeId='complianceGaugeChart'
                  shadowColor='rgba(80,205,137,0.5)'
                />

                <div className='d-flex flex-column text-center mt-5'>
                  <div className='d-flex justify-content-between'>
                    <div className='px-4 py-2 bg-light-success rounded-2 me-3'>
                      <div className='fs-7 text-gray-600'>Actual Readings</div>
                      <div className='fs-4 fw-bold text-success'>
                        {patientCompliance?.actualReadingsCount ?? 0}
                      </div>
                    </div>
                    <div className='px-4 py-2 bg-light-warning rounded-2'>
                      <div className='fs-7 text-gray-600'>Expected Readings</div>
                      <div className='fs-4 fw-bold text-warning'>
                        {patientCompliance?.expectedReadingsCount ?? 0}
                      </div>
                    </div>
                  </div>

                  <div className='mt-5'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <div className='text-muted fs-7'>
                        Critical
                        <br />
                        &lt;50%
                      </div>
                      <div className='text-muted fs-7'>
                        Poor
                        <br />
                        50-70%
                      </div>
                      <div className='text-muted fs-7'>
                        Average
                        <br />
                        70-85%
                      </div>
                      <div className='text-muted fs-7'>
                        Excellent
                        <br />
                        &gt;85%
                      </div>
                    </div>

                    <ProgressBar
                      /* value prop kept for API completeness; not displayed when customSegments used */
                      value={0}
                      customSegments={[
                        {color: 'bg-danger', width: '25%', rounded: 'start'},
                        {color: 'bg-warning', width: '25%'},
                        {color: 'bg-info', width: '25%'},
                        {color: 'bg-success', width: '25%', rounded: 'end'},
                      ]}
                      showMarker
                      markerPosition={Math.min(patientCompliance?.complianceRate ?? 0, 100)}
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  )
}

export default AdminDashboard
